import React from 'react';
import PropTypes from 'prop-types';

import moment from 'moment';

import GC, { dateTimeFormat } from '../../../../util/GlobalConstant';

import {
  Area,
  AreaChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';

import style from './index.scss';
import { ConfigData, getABData } from '../../../../util/ABUtil';
import { flattenChartData } from './chartsUtil';

/**
 * AlertsAndAnomaliesChart
 * @param props
 * @returns {*}
 * @constructor
 */
export const AlertsAndAnomaliesChart = (props) => {
  const { data, isLoading, isCollapsed, onTogglePanelVisibility, onReload } =
    props;

  const renderIsLoadingPanel = () => {
    return (
      <div className="card">
        <div className="card-header">
          <div className="row">
            <div className="col-md-6">
              <p>{getABData('Alerts & Anomalies')}</p>
            </div>

            <div className="col-md-6">
              <h5 className="text-right">
                <i
                  onClick={onTogglePanelVisibility}
                  className="fa fa-window-minimize"></i>
              </h5>
            </div>
          </div>
        </div>

        <div className="card-body">
          <div className="row">
            <div className="col-md-12" id="loading-message">
              <p>Loading {getABData('Alerts and Anomalies')} chart data...</p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderNoDataPanel = () => {
    return (
      <div className="card">
        <div className="card-header">
          <div className="row">
            <div className="col-md-6">
              <p>{getABData('Alerts & Anomalies')}</p>
            </div>

            <div className="col-md-6">
              <h5 className="text-right">
                <i
                  onClick={onTogglePanelVisibility}
                  className="fa fa-window-minimize"></i>
              </h5>
            </div>
          </div>
        </div>

        <div className="card-body">
          <div className="row">
            <div className="col-md-12" id="NoDataPanel">
              <p>
                No {getABData('Alerts and Anomalies')} data exists for the
                period selected.
              </p>
              <p>
                <a
                  className={style.retryLink}
                  onClick={onReload}>{`Click here to fetch ${getABData(
                  'alerts and anomalies'
                )} data again.`}</a>
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderMinimizedPanel = () => {
    return (
      <div className="card">
        <div className="card-header">
          <div className="row">
            <div className="col-md-6">
              <p>{getABData('Alerts & Anomalies')}</p>
            </div>

            <div className="col-md-6">
              <h5 className="text-right">
                <i
                  onClick={onTogglePanelVisibility}
                  className="fa fa-window-maximize"></i>
              </h5>
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (isCollapsed) {
    return renderMinimizedPanel();
  }

  if (isLoading) {
    return renderIsLoadingPanel();
  }

  if (Array.isArray(data) && data.length > 0) {
    let formattedData = [];
    data.forEach((item) => {
      const formattedItem = {
        ...item,
        date: moment
          .utc(item.date, [dateTimeFormat])
          .local()
          .format('DD/MM/YYYY')
      };

      formattedData.push(formattedItem);
    });

    let flattenedData = [];
    if (ConfigData.IS_NCD_NOT_AVAILABLE)
      flattenedData = flattenChartData(formattedData, ['ruleBased']);
    else
      flattenedData = flattenChartData(formattedData, [
        'ruleBased',
        'anomalies'
      ]);

    return (
      <div className="card">
        <div className="card-header">
          <div className="row">
            <div className="col-md-6">
              <p>{getABData('Alerts & Anomalies')}</p>
            </div>

            <div className="col-md-6">
              <h5 className="text-right">
                <i
                  onClick={onTogglePanelVisibility}
                  className="fa fa-window-minimize"></i>
              </h5>
            </div>
          </div>
        </div>

        <div className="card-body">
          <div className="row">
            <div className="col-md-12">
              {/* Figure Area Chart goes here */}
              <ResponsiveContainer
                width="100%"
                height={260}
                margin={{ top: 20, right: 30, left: 20, bottom: 10 }}>
                <AreaChart data={flattenedData}>
                  <defs>
                    {!ConfigData.IS_NCD_NOT_AVAILABLE && (
                      <linearGradient
                        id="colorAnomalies"
                        x1="0"
                        y1="0"
                        x2="0"
                        y2="1">
                        <stop
                          offset="5%"
                          stopColor={GC.anomalyColor}
                          stopOpacity={0.5}
                        />
                        <stop
                          offset="95%"
                          stopColor={GC.anomalyColor}
                          stopOpacity={0}
                        />
                      </linearGradient>
                    )}
                    <linearGradient
                      id="colorAlerts"
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1">
                      <stop
                        offset="5%"
                        stopColor={GC.ruleBasedColor}
                        stopOpacity={0.5}
                      />
                      <stop
                        offset="95%"
                        stopColor={GC.ruleBasedColor}
                        stopOpacity={0}
                      />
                    </linearGradient>
                  </defs>
                  <XAxis
                    height={60}
                    tickCount={8}
                    dataKey="date"
                    tickFormatter={dateTickFormatter}
                    tick={<CustomizedAxisTick />}
                  />
                  <YAxis />

                  <CartesianGrid strokeDasharray="5 5" vertical={false} />
                  <Tooltip content={<CustomTooltip data={flattenedData} />} />
                  <Legend formatter={legendFormatter} />
                  <Area
                    type="monotone"
                    dataKey="ruleBased"
                    stroke={GC.anomalyColor}
                    fillOpacity={0.8}
                    fill="url(#colorAnomalies)"
                  />
                  {!ConfigData.IS_NCD_NOT_AVAILABLE && (
                    <Area
                      type="monotone"
                      dataKey="anomalies"
                      stroke={GC.ruleBasedColor}
                      fillOpacity={0.8}
                      fill="url(#colorAlerts)"
                    />
                  )}
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return renderNoDataPanel();
};
/**
 * dateTickFormatter
 * @param tickitem
 * @returns {string}
 */
const dateTickFormatter = (tickitem) => {
  const tickDate = moment(tickitem, [dateTimeFormat]);
  if (tickDate.isValid()) {
    return moment(tickitem, [dateTimeFormat]).format('DD/MM/YYYY');
  }

  return '';
};
/**
 * CustomTooltip
 * @param props
 * @returns {null|*}
 * @constructor
 */
const CustomTooltip = (props) => {
  const { active, payload, text, label, data } = props;

  if (!data || !payload) {
    return null;
  }

  let pointLists = [];
  payload.forEach((item, i) => {
    let itemNameLabel = item.name;
    if (item.name === 'anomalies') {
      itemNameLabel = 'Anomalies';
    }

    if (item.name === 'ruleBased') {
      itemNameLabel = 'Alerts';
    }
    pointLists.push(
      <li key={i} style={{ color: `${item.stroke}` }}>
        {itemNameLabel} : <span style={{ float: 'right' }}>{item.value}</span>
      </li>
    );
  });

  let accumulatedLists = [];
  payload.forEach((item, i) => {
    let itemNameLabel = item.name;
    if (item.name === 'anomalies') {
      itemNameLabel = 'Anomalies';
    }

    if (item.name === 'ruleBased') {
      itemNameLabel = 'Alerts';
    }
    accumulatedLists.push(
      <li key={i} style={{ color: `${item.stroke}` }}>
        {itemNameLabel} :{' '}
        <span style={{ float: 'right' }}>
          {accumulater(data, label, item.name)}
        </span>
      </li>
    );
  });

  let startingDate = '';
  let selectedDate = '';

  const startingDateValue = moment(data[0].date, ['DD/MM/YYYY']);
  if (startingDateValue.isValid()) {
    startingDate = data[0].date;
  }

  const selectedDateValue = moment(label, ['DD/MM/YYYY']);
  if (selectedDateValue.isValid()) {
    selectedDate = label;
  }

  return active ? (
    <div className={style.customTooltip}>
      <div className="row">
        <div className="col-sm-12">
          <p>
            <i className="fa fa-calendar" /> {selectedDate}
          </p>
          <ul className="list-unstyled">{pointLists}</ul>
        </div>

        <svg height="10" width="180">
          <line
            x1="20"
            y1="2"
            x2="180"
            y2="2"
            style={{ stroke: 'rgb(133,133,0)', strokeWidth: 1 }}
          />
        </svg>

        <div className="col-sm-12">
          <p>
            <i className="fa fa-calendar" /> {startingDate} -> {selectedDate}
          </p>
          <ul className="list-unstyled">{accumulatedLists}</ul>
        </div>
      </div>
    </div>
  ) : null;
};
/**
 * accumulater
 * @param data
 * @param label
 * @param name
 * @returns {number}
 */
const accumulater = (data, label, name) => {
  let result = 0;

  let i = 0;
  let isLooping = true;
  while (isLooping) {
    const item = data[i];
    result += item[name];

    i++;

    if (item.date === label) {
      isLooping = false;
    }
  }

  return result;
};
/**
 * legendFormatter
 * @param value
 * @param entry
 * @param index
 * @returns {string}
 */
const legendFormatter = (value, entry, index) => {
  if (value) {
    if (value === 'anomalies') {
      return 'Anomalies';
    }

    if (value === 'ruleBased') {
      return 'Alerts';
    }
  }

  return '';
};
/**
 * CustomizedAxisTick
 * @param props
 * @returns {*}
 * @constructor
 */
const CustomizedAxisTick = (props) => {
  const { x, y, stroke, payload, visibleTicksCount, index } = props;
  const tickValue = moment(payload.value);

  if (tickValue.isValid()) {
    if (index === visibleTicksCount - 1) {
      return (
        <g transform={`translate(${x + 20},${y})`}>
          <text
            x={0}
            y={0}
            dy={12}
            textAnchor="end"
            fill="#666"
            transform="rotate(-35)">
            {payload.value}
          </text>
        </g>
      );
    } else {
      return (
        <g transform={`translate(${x},${y})`}>
          <text
            x={0}
            y={0}
            dy={12}
            textAnchor="end"
            fill="#666"
            transform="rotate(-35)">
            {payload.value}
          </text>
        </g>
      );
    }
  }

  if (index === visibleTicksCount - 1) {
    return (
      <g transform={`translate(${x + 20},${y})`}>
        <text
          x={0}
          y={0}
          dy={12}
          textAnchor="end"
          fill="#666"
          transform="rotate(-35)">
          {payload.value}
        </text>
      </g>
    );
  } else {
    return (
      <g transform={`translate(${x},${y})`}>
        <text
          x={0}
          y={0}
          dy={12}
          textAnchor="end"
          fill="#666"
          transform="rotate(-35)">
          {payload.value}
        </text>
      </g>
    );
  }
};
/**
 * AlertsAndAnomaliesChart
 * @type {{isLoading: shim, isCollapsed: shim, data: shim, onReload: shim, onTogglePanelVisibility: shim}}
 */
AlertsAndAnomaliesChart.propTypes = {
  data: PropTypes.array,
  isLoading: PropTypes.bool,
  isCollapsed: PropTypes.bool,
  onTogglePanelVisibility: PropTypes.func,
  onReload: PropTypes.func
};
